import { useMemo, useRef, useState } from "react";
import { bool, number, object } from "prop-types";
import { observer } from "mobx-react";
import { BreakdownColumns } from "@repo/utils";
import {
  LabelSelect,
  PinnableCell,
  useEstimateEditorSettings,
  useEstimateTableSettings,
  useVisibilityModeCellClassName,
} from "@tools";
import { Button, ObserverLabel, TooltipIcon } from "@components";
import { ArrowBack, Label, LabelFilled } from "@assets";
import { useTranslation } from "react-i18next";
import { calculateVisibleLabels } from "@utils";

export const TaskLabelsCell = observer(({
  element,
  columnSettings,
  isLastPinnedCell,
  tableDepth,
}) => {
  const { t } = useTranslation();
  const { allowEdition, isPresetEditor, isProposal, isPdfPrinterView } = useEstimateEditorSettings();
  const { artificialLabelColumnWidth } = useEstimateTableSettings();
  
  const [ showAll, setShowAll]  = useState(false);
  const [ showDialog, setShowDialog ] = useState(false);
  
  const ref = useRef(null);
  
  const { namedLabels } = element;
  const { pinLeftPx, hide } = columnSettings;
  
  const className = useVisibilityModeCellClassName(hide);
  
  const handleMenuOpen = () => {
    if(!allowEdition) return;
    setShowDialog((s) => !s);
  }
  const handleDialogClose = () => setShowDialog(() => false);
  
  const handleShowAll = () => {
    if ( isPresetEditor ) return;
    setShowAll(!showAll)
  }
  
  const labelWidths = useMemo(() => {
    return namedLabels.map(label => {
      const s = document.createElement("span");
      s.innerText = label.name;
      s.className = "px-2 label-title"
      
      window.document.body.appendChild(s);
      const w = s.getBoundingClientRect().width + 4; //margins
      window.document.body.removeChild(s);
      return w;
    })
  }, [namedLabels, namedLabels?.length]);
  
  const vEl = useMemo(() => (
    isPdfPrinterView
      ? namedLabels.length
      : calculateVisibleLabels(labelWidths, artificialLabelColumnWidth - 8 - (isProposal ? 0 : 24))
  ), [ artificialLabelColumnWidth, labelWidths, isPdfPrinterView ]);
  
  const visibleLabels = showAll ? namedLabels : namedLabels.slice(0, vEl);
  const rest = namedLabels.slice(vEl).length;
  
  return (
    <PinnableCell
      resizeable
      colName={ BreakdownColumns.LABELS }
      justifyContent="flex-start"
      allowOverflowDisplay={isLastPinnedCell}
      className={className}
      pinnedLeft={typeof pinLeftPx === "number"}
      tableDepth={tableDepth}
      wrap="wrap"
    >
      {/*<LabelList*/}
      {/*  labels={visibleLabels}*/}
      {/*  maxWidth="calc(100% - 32)"*/}
      {/*  className="noExpand"*/}
      {/*/>*/}
      {
        visibleLabels.map((label) => (
          <ObserverLabel
            key={label.id}
            label={label}
            className="noExpand my-0-5 mx-0-5"
            onClick={handleMenuOpen}
            ellipsisOnOverflow
            maxWidth="calc(100% - 32)"
          />
        ))
      }
      {Boolean(rest) && (
        <Button
          variant="contained"
          color="secondary"
          size="small"
          className="my-0-5 mx-0-5"
          style={{
            height: 30,
            width: 30,
            minWidth: 30,
          }}
          onClick={handleShowAll}
        >
          {
            showAll ? <ArrowBack /> : `+${rest}`
          }
        </Button>
      )}
      {
        allowEdition &&
        <TooltipIcon
          ref={ref}
          icon={visibleLabels?.length > 0 ? <LabelFilled /> : <Label />}
          onClick={handleMenuOpen}
          className="button-label-add noDrag hoverIcon opq"
          size="small"
          color="primary"
          tabIndex={-1}
        >
          {t("views.editor.labels.add_label")}
        </TooltipIcon>
      }
      {
        allowEdition && showDialog &&
        <LabelSelect
          show={showDialog}
          element={element}
          anchorEl={ref.current}
          onClose={handleDialogClose}
        />
      }
    </PinnableCell>
  )
})

TaskLabelsCell.propTypes = {
  element: object.isRequired,
  tableDepth: number.isRequired,
  isLastPinnedCell: bool,
}